// src/services/lyricSyncService.js
//
// Sing-along timing for topic songs.
//
// WHERE IT LIVES
// --------------
// lyricSync/{slug} — one document per topic, keyed by the same slug as
// topicLibrary/{slug}. It is a separate collection on purpose:
//
//   * topicLibrary documents are rewritten by the seeder and by the Topic
//     Library panel. Timing data riding along inside them would be one
//     careless setDoc away from being wiped.
//   * Timing belongs to ONE recording of a song. If the song file changes,
//     the timings are wrong, so the document remembers which file it was
//     made for (musicKey) and the player refuses to use it for any other.
//
// DOCUMENT SHAPE
// --------------
// {
//   slug, title,
//   musicUrl, musicKey,         // the song the timings were made against
//   lines: [...],               // the lyrics AS THEY WERE when synced
//   cues: [{ i, t, e?, w?, c?, low? }],
//                               // i = index into lines, t = start (sec)
//                               // e = end (sec, auto-sync only)
//                               // w = word start offsets from t (auto-sync,
//                               //     or "Tap by word" in the studio)
//                               // wt = true when w was tapped by hand
//                               // c = confidence 0-1 (auto only)
//                               // low = auto-sync was unsure, check by ear
//   source: "tap" | "auto",     // who made the timings last
//   reviewed: bool,             // a person has checked it in the studio
//   published: bool,            // students see it (Music popup)
//   theme: "coral" | ...,       // background colours (THEMES below)
//   logoOpacity: 0-0.6,         // big background logo strength, 0 = off
//   videoUrl, videoPath, videoSig, videoMadeAt,
//   autoStats: { coverage, lowCount, unmatched: [i] },
//   updatedAt, updatedBy
// }
//
// WHY `lines` IS A SNAPSHOT
// -------------------------
// A cue points at a line by its position. If someone edits the lyrics in the
// Topic Library afterwards — adds a blank row, fixes a typo, inserts a
// verse — every position after the edit shifts and the highlight lands on the
// wrong line. Keeping a copy of the lines the timings were made against means
// the player always shows the words that match the timings, and the studio
// can tell you the library lyrics have moved on and offer to carry the
// timings across (remapCues).
//
// A CUE IS A MOMENT, NOT A LINE
// -----------------------------
// Many songs write the chorus once but sing it three times. Cues are a
// time-ordered list of "line i starts now", so the same line can be lit
// any number of times. Nothing here assumes the song follows the page.

import {
  collection,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";
import { exactKey, keyCandidates } from "../utils/topicKey";

export const LYRIC_SYNC_COLLECTION = "lyricSync";

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

/**
 * The OLD key: letters and digits only. Kept for display and for reading
 * sing-alongs saved before twin topics got their own keys. New writes use the
 * key the caller passes (makeToolKey in src/utils/topicKey.js), unstripped,
 * so earth'swater and earthswater no longer share one sing-along.
 */
export const syncSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * A stable identity for a song file.
 *
 * Firebase download URLs carry a token that changes whenever someone
 * regenerates it, so comparing whole URLs would call the same file "changed".
 * The storage path inside /o/... is what actually identifies the recording.
 * align-lyrics.py computes the same key the same way.
 */
export const mediaKey = (url = "") => {
  const s = String(url || "");
  const m = s.match(/\/o\/([^?#]+)/);
  if (m) {
    try {
      return decodeURIComponent(m[1]);
    } catch {
      return m[1];
    }
  }
  return s.split(/[?#]/)[0];
};

/** Lyrics as an array of strings, whatever shape they were stored in. */
export const toLyricLines = (lyrics) => {
  if (Array.isArray(lyrics)) return lyrics.map((l) => String(l ?? ""));
  if (typeof lyrics === "string") return lyrics.split("\n");
  return [];
};

/** 65.3 -> "1:05.3" */
export const formatTime = (sec, digits = 1) => {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  const fixed = s.toFixed(digits);
  const pad = s < 10 ? "0" : "";
  return `${m}:${pad}${fixed}`;
};

/** "1:05.3" | "65.3" -> 65.3 ; null when it cannot be read. */
export const parseTime = (text) => {
  const s = String(text || "").trim();
  if (!s) return null;
  const parts = s.split(":");
  if (parts.length > 2) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
  return parts.length === 2 ? nums[0] * 60 + nums[1] : nums[0];
};

const round2 = (n) => Math.round(n * 100) / 100;

/* ------------------------------------------------------------------ */
/* Reading the lyrics: sections, pages, sung lines                     */
/* ------------------------------------------------------------------ */
//
// The same three rules are written out in align-lyrics.py. If you change
// one, change the other, or the script will time different lines than the
// studio shows.
//
//   "(Chorus)", "[verse 2]", "(Bridge 1):"  -> section heading, not sung
//   "© Smart Board Learning, LLC ..."        -> credit line, not sung
//   ""                                       -> break between sections

const HEADER_RE = /^\s*[([]\s*([^)\]]{1,40}?)\s*[)\]]\s*:?\s*$/;
const CREDIT_RE = /^\s*(©|\(c\)\s|copyright\b)/i;

export const isHeaderLine = (text) => HEADER_RE.test(String(text || ""));
export const isCreditLine = (text) => CREDIT_RE.test(String(text || ""));
export const isSungLine = (text) => {
  const s = String(text || "").trim();
  return s !== "" && !isHeaderLine(s) && !isCreditLine(s);
};

const prettyLabel = (raw) => {
  const s = String(raw || "")
    .trim()
    .replace(/([a-z])(\d)/gi, "$1 $2")
    .toLowerCase();
  return s.replace(/\b[a-z]/g, (c) => c.toUpperCase());
};

const MAX_LINES_PER_PAGE = 4;

/**
 * Turns the raw lines into what the player shows.
 *
 * A section is the run of sung lines under one heading (or between blank
 * rows). A page is what fits on screen at once: a long verse is split into
 * balanced pages — six lines become 3 + 3, not 4 + 2 — so a page never ends
 * on a lonely line.
 */
export const parseLyrics = (rawLines, { maxPerPage = MAX_LINES_PER_PAGE } = {}) => {
  const lines = toLyricLines(rawLines);
  const sections = [];
  const credits = [];
  let current = null;
  let pendingLabel = "";

  const close = () => {
    if (current && current.lineIdx.length) sections.push(current);
    current = null;
  };

  lines.forEach((text, i) => {
    const s = String(text || "").trim();
    if (s === "") {
      close();
      return;
    }
    if (isCreditLine(s)) {
      credits.push(s);
      return;
    }
    const header = s.match(HEADER_RE);
    if (header) {
      close();
      pendingLabel = prettyLabel(header[1]);
      return;
    }
    if (!current) {
      current = { label: pendingLabel, lineIdx: [] };
      pendingLabel = "";
    }
    current.lineIdx.push(i);
  });
  close();

  const pages = [];
  const pageOfLine = {};
  const sung = [];

  sections.forEach((section, sIdx) => {
    const n = section.lineIdx.length;
    const parts = Math.max(1, Math.ceil(n / maxPerPage));
    const size = Math.ceil(n / parts);
    for (let p = 0; p < parts; p += 1) {
      const lineIdx = section.lineIdx.slice(p * size, (p + 1) * size);
      if (!lineIdx.length) continue;
      const pageIndex = pages.length;
      pages.push({
        section: sIdx,
        label: section.label,
        part: p + 1,
        parts,
        lineIdx,
      });
      lineIdx.forEach((i) => {
        pageOfLine[i] = pageIndex;
        sung.push(i);
      });
    }
  });

  return { lines, sections, pages, pageOfLine, sung, credits };
};

/** The sung line after `i` in written order, or -1 at the end. */
export const nextSungLine = (parsed, i) => {
  const at = parsed.sung.indexOf(i);
  if (at === -1) return parsed.sung[0] ?? -1;
  return parsed.sung[at + 1] ?? -1;
};

export const wordsOf = (text) => String(text || "").trim().split(/\s+/).filter(Boolean);

/* ------------------------------------------------------------------ */
/* What is on screen at time t                                          */
/* ------------------------------------------------------------------ */

/** Cues sorted by time, bad entries dropped. Never mutates the input. */
export const cleanCues = (cues, parsed) =>
  (Array.isArray(cues) ? cues : [])
    .filter(
      (c) =>
        c &&
        Number.isFinite(Number(c.t)) &&
        Number(c.t) >= 0 &&
        (!parsed || parsed.pageOfLine[c.i] !== undefined)
    )
    .map((c) => ({ ...c, t: Number(c.t) }))
    .sort((a, b) => a.t - b.t);

/**
 * When the highlight on a line should finish sweeping.
 *
 * Auto-sync knows the real end (e). Tapped cues only know starts, so the end
 * is the next tap — capped by a reading-speed estimate, so a line followed by
 * a long instrumental does not crawl for twenty seconds.
 */
export const cueEnd = (cue, next, parsed) => {
  const nextT = next ? next.t : Infinity;
  if (Number.isFinite(cue.e) && cue.e > cue.t) return Math.min(cue.e, nextT);
  const words = wordsOf(parsed?.lines?.[cue.i]).length || 4;
  const estimate = cue.t + Math.min(9, Math.max(1.4, words * 0.45 + 0.5));
  return Math.min(estimate, Math.max(cue.t + 0.2, nextT - 0.05));
};

const lastCueAtOrBefore = (cues, t) => {
  let lo = 0;
  let hi = cues.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].t <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
};

/**
 * Which words of the active line are lit.
 *
 * Uses real word timings when auto-sync stored them (w) and the line still
 * has the same number of words; otherwise spreads the words across the line's
 * duration by length, which reads naturally for sung text.
 */
const litWordCount = (cue, end, t, words) => {
  const n = words.length;
  if (!n) return 0;
  if (Array.isArray(cue.w) && cue.w.length === n) {
    let lit = 0;
    for (let k = 0; k < n; k += 1) if (t >= cue.t + Number(cue.w[k]) - 0.02) lit = k + 1;
    return Math.max(1, lit);
  }
  const span = Math.max(0.2, end - cue.t);
  const frac = Math.min(1, Math.max(0, (t - cue.t) / span));
  const weights = words.map((w) => w.length + 1);
  const total = weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  let lit = 0;
  for (let k = 0; k < n; k += 1) {
    if (frac * total >= acc - 0.0001) lit = k + 1;
    acc += weights[k];
  }
  return Math.max(1, lit);
};

/**
 * Everything a renderer needs for one frame. Shared by the on-screen player
 * and the MP4 canvas renderer so the two can never disagree.
 *
 * mode:
 *   "empty" — no sung lines at all
 *   "intro" — before the first cue; shows the first page, nothing lit
 *   "sing"  — a line is active
 *   "break" — instrumental gap; shows the NEXT page, nothing lit, countdown
 *   "end"   — after the last line has finished
 */
export const frameAt = (parsed, cues, t) => {
  if (!parsed || !parsed.pages.length) {
    return { mode: "empty", page: null, pageIndex: -1 };
  }

  const k = lastCueAtOrBefore(cues, t);

  if (k === -1) {
    const first = cues[0];
    const pageIndex = first ? parsed.pageOfLine[first.i] ?? 0 : 0;
    return {
      mode: "intro",
      pageIndex,
      page: parsed.pages[pageIndex],
      activeLine: null,
      countdown: first ? first.t - t : null,
      lineState: {},
    };
  }

  const cue = cues[k];
  const next = cues[k + 1] || null;
  const end = cueEnd(cue, next, parsed);

  if (next && t > end + 1.2 && next.t - t > 2.0) {
    const pageIndex = parsed.pageOfLine[next.i] ?? 0;
    return {
      mode: "break",
      pageIndex,
      page: parsed.pages[pageIndex],
      activeLine: null,
      countdown: next.t - t,
      lineState: {},
    };
  }

  if (!next && t > end + 3) {
    const pageIndex = parsed.pageOfLine[cue.i] ?? 0;
    const lineState = {};
    parsed.pages[pageIndex].lineIdx.forEach((i) => {
      lineState[i] = "sung";
    });
    return {
      mode: "end",
      pageIndex,
      page: parsed.pages[pageIndex],
      activeLine: null,
      countdown: null,
      lineState,
    };
  }

  const pageIndex = parsed.pageOfLine[cue.i] ?? 0;
  const page = parsed.pages[pageIndex];
  const pos = page.lineIdx.indexOf(cue.i);
  const lineState = {};
  page.lineIdx.forEach((i, idx) => {
    lineState[i] = idx < pos ? "sung" : idx === pos ? "active" : "upcoming";
  });

  const words = wordsOf(parsed.lines[cue.i]);

  let nextPreview = null;
  if (next) {
    const nextPage = parsed.pageOfLine[next.i];
    if (nextPage !== pageIndex && nextPage !== undefined) {
      nextPreview = {
        label: parsed.pages[nextPage].label,
        text: parsed.lines[parsed.pages[nextPage].lineIdx[0]],
      };
    }
  }

  return {
    mode: "sing",
    pageIndex,
    page,
    activeLine: cue.i,
    litWords: litWordCount(cue, end, t, words),
    progress: Math.min(1, Math.max(0, (t - cue.t) / Math.max(0.2, end - cue.t))),
    lineState,
    nextPreview,
    countdown: null,
  };
};

/* ------------------------------------------------------------------ */
/* Carrying timings across a lyrics edit                               */
/* ------------------------------------------------------------------ */

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[‘’`]/g, "'")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Re-points cues from oldLines to newLines by matching line text.
 *
 * Duplicate lines are matched by occurrence: the 2nd "What do we hear
 * making that sound?" in the old lyrics maps to the 2nd in the new ones.
 * Cues whose line no longer exists are dropped and counted, so the studio
 * can say exactly how many need re-tapping.
 */
export const remapCues = (oldLines, newLines, cues) => {
  const oldParsed = parseLyrics(oldLines);
  const newParsed = parseLyrics(newLines);

  const occurrenceKey = (parsed) => {
    const seen = {};
    const map = {};
    parsed.sung.forEach((i) => {
      const key = norm(parsed.lines[i]);
      seen[key] = (seen[key] || 0) + 1;
      map[i] = `${key}#${seen[key]}`;
    });
    return map;
  };

  const oldKeys = occurrenceKey(oldParsed);
  const newKeys = occurrenceKey(newParsed);
  const newByKey = {};
  Object.entries(newKeys).forEach(([i, key]) => {
    newByKey[key] = Number(i);
  });

  let dropped = 0;
  const out = [];
  (cues || []).forEach((c) => {
    const key = oldKeys[c.i];
    const target = key !== undefined ? newByKey[key] : undefined;
    if (target === undefined) {
      dropped += 1;
      return;
    }
    out.push({ ...c, i: target });
  });

  return { cues: out, dropped };
};

/** True when two lyric arrays differ in anything that matters. */
export const linesDiffer = (a, b) => {
  const x = toLyricLines(a);
  const y = toLyricLines(b);
  if (x.length !== y.length) return true;
  return x.some((line, idx) => String(line).trim() !== String(y[idx]).trim());
};

/* ------------------------------------------------------------------ */
/* Look: backgrounds, logo, copyright (shared by player and MP4)       */
/* ------------------------------------------------------------------ */
//
// BACKGROUNDS
// -----------
// Fourteen warm, low-glare backgrounds, plus the original SBL blue. Every one
// was checked for contrast: the words a child is reading stay at least 4.5:1
// against both ends of the gradient (lyrics are big, bold text, so 3:1 is
// the formal minimum), the lit colour at least 3:1, and the dimmed "coming
// up" lines at least 3:1. Two families:
//
//   deep  — white words on a rich, muted colour (better on a smart board in
//           a bright room)
//   soft  — dark words on a pale colour (gentlest on the eyes up close)
//
// `logo` picks which logo file sits behind the words: white on deep
// backgrounds, black on soft ones. `band` is the strip behind the line being
// sung; `edge` is the thin outline the MP4 draws around words.

const deep = (label, bg, lit, extra = {}) => ({
  label,
  family: "deep",
  bg,
  ink: "#ffffff",
  dim: "rgba(255,255,255,0.62)",
  sung: "rgba(255,255,255,0.82)",
  lit,
  pill: "rgba(255,255,255,0.18)",
  band: "rgba(0,0,0,0.18)",
  edge: "rgba(0,0,0,0.22)",
  logo: "white",
  ...extra,
});

const soft = (label, bg, ink, lit, inkRgb) => ({
  label,
  family: "soft",
  bg,
  ink,
  dim: `rgba(${inkRgb},0.6)`,
  sung: `rgba(${inkRgb},0.8)`,
  lit,
  pill: `rgba(${inkRgb},0.1)`,
  band: "rgba(255,255,255,0.55)",
  edge: "rgba(255,255,255,0.35)",
  logo: "black",
});

export const THEMES = {
  sbl: deep("SBL Blue (original)", ["#2a4dff", "#8b5cf6"], "#ffd23f", {
    dim: "rgba(255,255,255,0.76)",
    sung: "rgba(255,255,255,0.88)",
    band: "rgba(0,0,0,0.16)",
  }),
  coral: deep("Sunset Coral", ["#983624", "#b04f38"], "#fff1a8", {
    dim: "rgba(255,255,255,0.7)",
    sung: "rgba(255,255,255,0.85)",
  }),
  terracotta: deep("Terracotta", ["#823d24", "#a35637"], "#ffe39a"),
  berry: deep("Berry", ["#6e2645", "#9b3d62"], "#ffd29c"),
  plum: deep("Plum Dusk", ["#432851", "#6e4475"], "#ffd97a"),
  cocoa: deep("Cocoa", ["#4e3228", "#71503f"], "#ffd27f"),
  forest: deep("Forest", ["#25503f", "#40705a"], "#ffe08a"),
  teal: deep("Deep Teal", ["#1a4a54", "#2a6b6d"], "#ffe29a"),
  twilight: deep("Twilight", ["#26335c", "#43517f"], "#ffd479"),
  peach: soft("Peach Cream", ["#ffe6d3", "#ffcfae"], "#3b2418", "#b8360b", "59,36,24"),
  butter: soft("Butter", ["#fff5d1", "#ffe3a0"], "#3a2e0b", "#a33f06", "58,46,11"),
  sage: soft("Sage", ["#e6f0df", "#c7dbbd"], "#1f3324", "#9a3412", "31,51,36"),
  blush: soft("Blush", ["#fde4e6", "#f7c9d0"], "#3d1f2a", "#9d174d", "61,31,42"),
  sand: soft("Sand", ["#f5e9d8", "#e4ceae"], "#3a2a1a", "#9a3412", "58,42,26"),
  mist: soft("Morning Mist", ["#e3effa", "#c5daf0"], "#1a2b44", "#b8360b", "26,43,68"),
};

/** Picker order: original first, then deep colours, then soft ones. */
export const THEME_ORDER = Object.keys(THEMES);

// Sing-alongs saved before this palette existed name one of the first five
// themes. Each maps to its nearest new colour so nothing saved breaks.
const LEGACY_THEMES = {
  sunny: "sbl",
  ocean: "teal",
  sunset: "coral",
  night: "twilight",
};

export const DEFAULT_THEME = "sbl";
export const themeKeyOf = (key) =>
  THEMES[key] ? key : LEGACY_THEMES[key] && THEMES[LEGACY_THEMES[key]] ? LEGACY_THEMES[key] : DEFAULT_THEME;
export const themeOf = (key) => THEMES[themeKeyOf(key)];

// LOGO
// ----
// The same two files every game already uses. They live in Storage, so the
// MP4 renderer loads them with crossOrigin="anonymous"; your bucket's CORS
// setting already allows GET from the portal's own addresses, which is what
// that needs. If the logo can't be loaded, the video is still made, without
// it, and the studio says so.
export const SBL_LOGOS = {
  white:
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoWhiteTM.png?alt=media&token=6ef84cf6-6fd9-4b56-bf05-aea9a1784c72",
  black:
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f",
};

export const logoUrlFor = (themeKey) => SBL_LOGOS[themeOf(themeKey).logo] || SBL_LOGOS.white;

// How strongly the big background logo shows, 0–0.6 (0 = hidden).
// 0.15 is the default: clearly there, and still quiet enough that the
// lyrics in front of it read cleanly from the back of a classroom.
export const DEFAULT_LOGO_OPACITY = 0.15;
export const MAX_LOGO_OPACITY = 0.6;
export const logoOpacityOf = (sync) => {
  const v = Number(sync?.logoOpacity);
  if (!Number.isFinite(v)) return DEFAULT_LOGO_OPACITY;
  return Math.min(MAX_LOGO_OPACITY, Math.max(0, v));
};

export const COPYRIGHT_TEXT = "© Smart Board Learning. All rights reserved.";

/* ------------------------------------------------------------------ */
/* Is this sync usable for this song?                                  */
/* ------------------------------------------------------------------ */

/**
 * The player's single gate. A sync is shown to students only when it is
 * published, has at least one cue, and was made for THIS song file.
 */
/**
 * Every song file these timings fit: the one they were saved against, plus,
 * once a faded MP3 has been made, the faded file AND the original it was cut
 * from. The faded song is the same recording up to the end marker, so the
 * timings are identical on both — a page still pointing at the original and
 * a page using the faded one both keep their sing-along.
 */
export const songKeysOf = (sync) =>
  [sync?.musicKey || mediaKey(sync?.musicUrl), sync?.fadedMusicKey, sync?.fadedSourceKey].filter(Boolean);

export const syncMatchesSong = (sync, musicUrl) =>
  Boolean(
    sync &&
      Array.isArray(sync.cues) &&
      sync.cues.length > 0 &&
      musicUrl &&
      songKeysOf(sync).includes(mediaKey(musicUrl))
  );

/** True when musicUrl IS this sing-along's faded MP3. */
export const isFadedSong = (sync, musicUrl) =>
  Boolean(sync?.fadedMusicKey && musicUrl && mediaKey(musicUrl) === sync.fadedMusicKey);

/**
 * The full-length recording to work from. When the topic's song is already
 * the faded MP3, the studio and the exporters go back to the original, so the
 * end can still be moved later and nothing is faded twice.
 */
export const fullSongUrlOf = (sync, musicUrl) =>
  isFadedSong(sync, musicUrl) && sync.fadedSourceUrl ? sync.fadedSourceUrl : musicUrl;

/**
 * A faded MP3 already fades itself and ends at the stop time. Players check
 * this so they don't fade its sound a second time (that would make the fade
 * sound quicker than the picture's). Half a second of slack for MP3 padding.
 */
export const fileFadesItself = (duration, stopAt) =>
  Number.isFinite(duration) && duration > 0 && stopAt !== null && duration <= stopAt + 0.5;

export const isSyncLive = (sync, musicUrl) =>
  Boolean(sync?.published) && syncMatchesSong(sync, musicUrl);

/* ------------------------------------------------------------------ */
/* Ending: "End sing-along here" + a 5-second fade                     */
/* ------------------------------------------------------------------ */

/** How long the screen and the song take to fade out after the end marker. */
export const FADE_SECONDS = 5;

/** The saved end marker in seconds, or null when the song just plays out. */
export const endAtOf = (sync) => {
  const v = Number(sync?.endAt);
  return Number.isFinite(v) && v > 0 ? round2(v) : null;
};

/** When playback actually stops: end marker + the fade. null = song end. */
export const stopTimeOf = (endAt) =>
  Number.isFinite(endAt) && endAt > 0 ? endAt + FADE_SECONDS : null;

/**
 * How visible / loud the sing-along is at time t: 1 before the end marker,
 * sliding to 0 over FADE_SECONDS, 0 after. The studio, the student player and
 * the video all use this, so they fade identically.
 */
export const fadeLevelAt = (endAt, t) => {
  if (!Number.isFinite(endAt) || endAt <= 0 || !(t > endAt)) return 1;
  return Math.max(0, 1 - (t - endAt) / FADE_SECONDS);
};

/** A short fingerprint of what the video shows, to spot a stale MP4. */
export const syncSignature = ({ lines, cues, theme, title, logoOpacity, endAt }) => {
  const parts = [
    toLyricLines(lines),
    (cues || []).map((c) => [c.i, round2(Number(c.t))]),
    themeKeyOf(theme),
    title || "",
    round2(logoOpacityOf({ logoOpacity })),
  ];
  // Only added when set, so every video made before the end marker existed
  // keeps its "up to date" status.
  const end = endAtOf({ endAt });
  if (end !== null) parts.push(["end", end]);
  const text = JSON.stringify(parts);
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

/* ------------------------------------------------------------------ */
/* Firestore                                                           */
/* ------------------------------------------------------------------ */

/**
 * Exact key first, stripped key second. A topic with its own sing-along gets
 * its own; one without falls back to the shared one (whose musicKey still has
 * to match the topic's song before isSyncLive shows it).
 */
export const getLyricSync = async (slug) => {
  for (const id of keyCandidates(slug)) {
    const snap = await getDoc(doc(db, LYRIC_SYNC_COLLECTION, id));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
  }
  return null;
};

export const getAllLyricSyncs = async () => {
  const snap = await getDocs(collection(db, LYRIC_SYNC_COLLECTION));
  const out = {};
  snap.forEach((d) => {
    out[d.id] = { id: d.id, ...d.data() };
  });
  return out;
};

/** Strips UI-only fields and rounds times so documents stay small. */
export const cuesForSave = (cues) =>
  (cues || []).map((c) => {
    const out = { i: Number(c.i), t: round2(Number(c.t)) };
    if (Number.isFinite(c.e)) out.e = round2(Number(c.e));
    if (Array.isArray(c.w) && c.w.length) out.w = c.w.map((x) => round2(Number(x)));
    if (Number.isFinite(c.c)) out.c = round2(Number(c.c));
    if (c.low) out.low = true;
    // Word-tapped in the Sing-along studio (Tap by word). Kept so a nudge
    // later moves the whole line instead of throwing the word times away.
    if (c.wt) out.wt = true;
    return out;
  });

/** Merges, so fields this caller did not send (e.g. videoUrl) survive. */
export const saveLyricSync = async (slug, data) => {
  const id = exactKey(slug);
  if (!id) throw new Error("A sing-along needs a topic slug.");
  const user = getAuth().currentUser;
  const payload = {
    ...data,
    slug: id,
    updatedAt: serverTimestamp(),
    updatedBy: user?.email || user?.uid || "unknown",
  };
  if (payload.cues) payload.cues = cuesForSave(payload.cues);
  if (payload.musicUrl) payload.musicKey = mediaKey(payload.musicUrl);
  await setDoc(doc(db, LYRIC_SYNC_COLLECTION, id), payload, { merge: true });
  return payload;
};

export const deleteLyricSync = async (slug) => {
  const id = exactKey(slug);
  if (!id) return;
  await deleteDoc(doc(db, LYRIC_SYNC_COLLECTION, id));
};
